import { Locales } from '@griply/types';
import { FrequencyUnit } from 'shared/types/habit-schedule';

const localeWordMap: Record<
  Locales,
  {
    time: string;
    times: string;
    per: string;
    day: string;
    week: string;
    month: string;
    year: string;
  }
> = {
  [Locales.NlNL]: {
    time: 'keer',
    times: 'keer',
    per: 'per',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
  },
  [Locales.EsES]: {
    time: 'vez',
    times: 'veces',
    per: 'por',
    day: 'día',
    week: 'semana',
    month: 'mes',
    year: 'año',
  },
  [Locales.DeDE]: {
    time: 'mal',
    times: 'mal',
    per: 'pro',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
  },
  [Locales.EnUS]: {
    time: 'time',
    times: 'times',
    per: 'per',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
  },
  // "2 razy w tygodniu" — the period takes the locative after "w".
  [Locales.PlPL]: {
    time: 'raz',
    times: 'razy',
    per: 'w',
    day: 'dniu',
    week: 'tygodniu',
    month: 'miesiącu',
    year: 'roku',
  },
  [Locales.DaDK]: {
    time: 'gang',
    times: 'gange',
    per: 'om',
    day: 'dagen',
    week: 'ugen',
    month: 'måneden',
    year: 'året',
  },
  [Locales.SvSE]: {
    time: 'gång',
    times: 'gånger',
    per: 'i',
    day: 'dagen',
    week: 'veckan',
    month: 'månaden',
    year: 'året',
  },
  [Locales.NbNO]: {
    time: 'gang',
    times: 'ganger',
    per: 'i',
    day: 'dagen',
    week: 'uken',
    month: 'måneden',
    year: 'året',
  },
  [Locales.ItIT]: {
    time: 'volta',
    times: 'volte',
    per: 'a',
    day: 'giorno',
    week: 'settimana',
    month: 'mese',
    year: 'anno',
  },
  [Locales.FrFR]: {
    time: 'fois',
    times: 'fois',
    per: 'par',
    day: 'jour',
    week: 'semaine',
    month: 'mois',
    year: 'an',
  },
  [Locales.PtPT]: {
    time: 'vez',
    times: 'vezes',
    per: 'por',
    day: 'dia',
    week: 'semana',
    month: 'mês',
    year: 'ano',
  },
  [Locales.PtBR]: {
    time: 'vez',
    times: 'vezes',
    per: 'por',
    day: 'dia',
    week: 'semana',
    month: 'mês',
    year: 'ano',
  },
};

const localeRegexWordMap: Record<
  Locales,
  {
    times: string;
    per: string;
    day: string;
    week: string;
    month: string;
    year: string;
  }
> = {
  [Locales.NlNL]: {
    times: 'keer',
    per: '(?:per|in de|in het)',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
  },
  [Locales.EsES]: {
    times: '(?:veces|vez)',
    per: '(?:por|a la|al)',
    day: 'd[íi]a',
    week: 'semana',
    month: 'mes',
    year: 'año',
  },
  [Locales.DeDE]: {
    times: 'mal',
    per: 'pro',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
  },
  [Locales.EnUS]: {
    times: 'times?',
    per: '(?:per|a)',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
  },
  [Locales.PlPL]: {
    times: '(?:razy|raz)',
    per: '(?:w|na)',
    day: '(?:dzień|dniu|dzien)',
    week: '(?:tydzień|tygodniu|tydzien)',
    month: '(?:miesiąc|miesiącu|miesiac|miesiacu)',
    year: '(?:rok|roku)',
  },
  [Locales.DaDK]: {
    times: '(?:gange|gang)',
    per: '(?:om|per|pr)',
    day: '(?:dagen|dag)',
    week: '(?:ugen|uge)',
    month: '(?:måneden|måned|maaneden|maaned)',
    year: '(?:året|år|aaret|aar)',
  },
  [Locales.SvSE]: {
    times: '(?:gånger|gång|ganger|gang)',
    per: '(?:i|per)',
    day: '(?:dagen|dag)',
    week: '(?:veckan|vecka)',
    month: '(?:månaden|månad|manaden|manad)',
    year: '(?:året|år|aret|ar)',
  },
  [Locales.NbNO]: {
    times: '(?:ganger|gang)',
    per: '(?:i|per)',
    day: '(?:dagen|dag)',
    week: '(?:uken|uke)',
    month: '(?:måneden|måned|maaneden|maaned)',
    year: '(?:året|år|aaret|aar)',
  },
  [Locales.ItIT]: {
    times: '(?:volte|volta)',
    per: '(?:alla|al|per|a)',
    day: 'giorno',
    week: 'settimana',
    month: 'mese',
    year: 'anno',
  },
  [Locales.FrFR]: {
    times: 'fois',
    per: '(?:par)',
    day: 'jour',
    week: 'semaine',
    month: 'mois',
    year: '(?:année|annee|an)',
  },
  [Locales.PtPT]: {
    times: '(?:vezes|vez)',
    per: '(?:por|na|no)',
    day: 'dia',
    week: 'semana',
    month: '(?:mês|mes)',
    year: 'ano',
  },
  [Locales.PtBR]: {
    times: '(?:vezes|vez)',
    per: '(?:por|na|no)',
    day: 'dia',
    week: 'semana',
    month: '(?:mês|mes)',
    year: 'ano',
  },
};

// Generates the regex pattern for targets like "5 times per week".
export const getTargetPatternContent = (language: Locales) => {
  const keywords =
    localeRegexWordMap[language] ?? localeRegexWordMap[Locales.EnUS];
  return `(\\d+)\\s+${keywords.times}\\s+${keywords.per}\\s+(${keywords.week}|${keywords.month}|${keywords.year})`;
};

/**
 * Parses a target string (e.g., "2 times per day") into a structured object.
 */
export const parseTarget = (targetStr: string, language: Locales) => {
  if (!targetStr) {
    return null;
  }

  const targetPattern = getTargetPatternContent(language);
  const match = targetStr
    .toLowerCase()
    .match(new RegExp(`^${targetPattern}$`, 'i'));
  if (match) {
    const count = parseInt(match[1], 10);
    const periodStr = match[2];
    const wordMap: Record<Locales, Record<string, string>> = {
      [Locales.EnUS]: {
        day: 'day',
        week: 'week',
        month: 'month',
        year: 'year',
      },
      [Locales.NlNL]: {
        dag: 'day',
        week: 'week',
        maand: 'month',
        jaar: 'year',
      },
      [Locales.EsES]: {
        día: 'day',
        semana: 'week',
        mes: 'month',
        año: 'year',
      },
      [Locales.DeDE]: {
        tag: 'day',
        woche: 'week',
        monat: 'month',
        jahr: 'year',
      },
      [Locales.PlPL]: {
        dzień: 'day',
        dzien: 'day',
        dniu: 'day',
        tydzień: 'week',
        tydzien: 'week',
        tygodniu: 'week',
        miesiąc: 'month',
        miesiac: 'month',
        miesiącu: 'month',
        miesiacu: 'month',
        rok: 'year',
        roku: 'year',
      },
      [Locales.DaDK]: {
        dagen: 'day',
        dag: 'day',
        ugen: 'week',
        uge: 'week',
        måneden: 'month',
        måned: 'month',
        maaneden: 'month',
        maaned: 'month',
        året: 'year',
        år: 'year',
        aaret: 'year',
        aar: 'year',
      },
      [Locales.SvSE]: {
        dagen: 'day',
        dag: 'day',
        veckan: 'week',
        vecka: 'week',
        månaden: 'month',
        månad: 'month',
        manaden: 'month',
        manad: 'month',
        året: 'year',
        år: 'year',
        aret: 'year',
        ar: 'year',
      },
      [Locales.NbNO]: {
        dagen: 'day',
        dag: 'day',
        uken: 'week',
        uke: 'week',
        måneden: 'month',
        måned: 'month',
        maaneden: 'month',
        maaned: 'month',
        året: 'year',
        år: 'year',
        aaret: 'year',
        aar: 'year',
      },
      [Locales.ItIT]: {
        giorno: 'day',
        settimana: 'week',
        mese: 'month',
        anno: 'year',
      },
      [Locales.FrFR]: {
        jour: 'day',
        semaine: 'week',
        mois: 'month',
        an: 'year',
        année: 'year',
        annee: 'year',
      },
      [Locales.PtPT]: {
        dia: 'day',
        semana: 'week',
        mês: 'month',
        mes: 'month',
        ano: 'year',
      },
      [Locales.PtBR]: {
        dia: 'day',
        semana: 'week',
        mês: 'month',
        mes: 'month',
        ano: 'year',
      },
    };
    const periodEN = wordMap[language][periodStr] ?? periodStr;
    return {
      count,
      unit: periodEN as FrequencyUnit,
    };
  }
  return null;
};

export const parseTargetToString = (
  target: { count: number; unit: FrequencyUnit },
  language: Locales,
) => {
  const words = localeWordMap[language] ?? localeWordMap[Locales.EnUS];
  const frequency =
    target.unit === FrequencyUnit.Week
      ? words.week
      : target.unit === FrequencyUnit.Month
        ? words.month
        : words.year;
  return `${target.count} ${target.count === 1 ? words.time : words.times} ${words.per} ${frequency}`;
};
